import { Link } from 'react-router-dom';
import { ArrowRight, Camera, CheckCircle2, Gift, HeartHandshake, Images, Palette, Sparkles, Users } from 'lucide-react';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { absoluteUrl, SITE_NAME } from '@/constants/site';
import { trackFunnelEvent } from '@/services/FunnelAnalytics';

const photoIdeas = [
  {
    icon: Users,
    title: 'Tous les petits-enfants',
    photo: 'Un portrait de fratrie, une réunion de famille ou un moment spontané où les visages restent nets et assez proches.',
    style: 'Aquarelle pour une ambiance douce, peinture à l’huile pour un portrait familial plus classique.',
    source: 'grandchildren',
  },
  {
    icon: HeartHandshake,
    title: 'Un souvenir partagé',
    photo: 'Des vacances, une fête familiale, un jardin, une maison ou un lieu qui raconte une histoire commune.',
    style: 'Impressionniste pour le décor, croquis pour un souvenir sobre et facile à intégrer.',
    source: 'shared_memory',
  },
  {
    icon: Images,
    title: 'Une photo ancienne',
    photo: 'Un portrait de jeunesse ou de famille bien cadré, avec suffisamment de contraste et sans détail essentiel coupé.',
    style: 'Huile ou croquis pour conserver le caractère du souvenir sans surcharger la composition.',
    source: 'old_photo',
  },
];

const photoChecklist = [
  'Choisissez une image que vos grands-parents reconnaîtront immédiatement et qui porte une histoire familiale.',
  'Préférez des visages assez grands, nets et bien éclairés plutôt qu’un groupe très éloigné.',
  'Pour une photo ancienne, vérifiez le contraste et évitez les reflets, captures d’écran ou plis très visibles.',
  'Si plusieurs générations apparaissent, gardez une composition simple pour que chaque personne reste lisible.',
];

const styleSuggestions = [
  {
    to: '/styles/watercolor',
    title: 'Aquarelle',
    helper: 'Pour une photo lumineuse de petits-enfants ou un souvenir familial doux et chaleureux.',
  },
  {
    to: '/styles/oil-painting',
    title: 'Peinture à l’huile',
    helper: 'Pour donner à un portrait de famille un rendu intemporel adapté à une pièce de vie.',
  },
  {
    to: '/styles/impressionist',
    title: 'Impressionniste',
    helper: 'Pour valoriser une maison, un jardin, un paysage de vacances ou un lieu de famille.',
  },
  {
    to: '/styles/sketch',
    title: 'Croquis',
    helper: 'Pour une photo ancienne ou un portrait simple qui appelle un rendu sobre et délicat.',
  },
];

const planningSteps = [
  {
    title: '1. Partir de leur histoire',
    text: 'Petits-enfants, maison de famille, voyage ou photo de jeunesse : choisissez d’abord ce qui leur parlera personnellement.',
  },
  {
    title: '2. Vérifier la lisibilité',
    text: 'Une photo de famille fonctionne mieux si les visages sont reconnaissables et si l’arrière-plan ne détourne pas l’attention.',
  },
  {
    title: '3. Adapter le style à leur intérieur',
    text: 'Un rendu aquarelle, huile, impressionniste ou croquis doit renforcer le souvenir et pouvoir trouver naturellement sa place chez eux.',
  },
];

const relatedPages = [
  {
    to: '/blog/cadeau-grands-parents-photo-famille',
    title: 'Guide cadeau grands-parents',
    helper: 'Approfondir les idées de photos de famille et les occasions avant de lancer la création.',
  },
  {
    to: '/idees-cadeaux-photo-personnalises',
    title: 'Toutes les idées cadeaux photo',
    helper: 'Comparer les pistes pour un anniversaire, Noël, un mariage, une famille ou un animal.',
  },
  {
    to: '/tableau-photo-personnalise',
    title: 'Tableau photo personnalisé',
    helper: 'Comprendre le parcours complet, de la photo et du style jusqu’au support.',
  },
];

const createPath = (source: string) => `/create?source=grandparents_gift_${source}`;

const trackGrandparentsCta = (source: string) => {
  trackFunnelEvent('seo_landing_cta_clicked', {
    page: 'cadeau-grands-parents-photo-personnalise',
    source: `grandparents_gift_${source}`,
  });
};

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': absoluteUrl('/cadeau-grands-parents-photo-personnalise'),
      name: 'Cadeau grands-parents photo personnalisé',
      description:
        'Guide pour créer un cadeau photo personnalisé pour ses grands-parents : photo de famille, petits-enfants, souvenir partagé et styles artistiques.',
      url: absoluteUrl('/cadeau-grands-parents-photo-personnalise'),
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: absoluteUrl(),
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil',
          item: absoluteUrl(),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cadeau grands-parents photo personnalisé',
          item: absoluteUrl('/cadeau-grands-parents-photo-personnalise'),
        },
      ],
    },
  ],
};

const CadeauGrandsParentsPhotoPersonnalise = () => (
  <>
    <SEOHead
      title="Cadeau grands-parents photo personnalisé | Ma Photo en Tableau"
      description="Créez un cadeau photo personnalisé pour vos grands-parents : conseils pour choisir une photo de famille, un souvenir et un style de tableau adapté."
      canonical={absoluteUrl('/cadeau-grands-parents-photo-personnalise')}
      schema={schema}
    />
    <div className="min-h-screen overflow-x-hidden">
      <main>
        <section className="section-shell gradient-hero relative overflow-hidden pb-20 pt-32">
          <div className="hero-orb left-[8%] top-28 h-32 w-32 bg-accent/40" />
          <div className="hero-orb right-[12%] top-24 h-40 w-40 bg-primary/25 [animation-delay:2s]" />
          <div className="container relative mx-auto px-6">
            <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
              <div className="max-w-3xl">
                <p className="eyebrow mb-4 inline-flex rounded-full border border-primary/15 bg-white/55 px-4 py-2 text-primary/85 shadow-card backdrop-blur-sm">
                  Cadeau grands-parents photo personnalisé
                </p>
                <h1 className="display-title mb-6 text-foreground">
                  Transformez une histoire de famille en <span className="text-premium">cadeau pour vos grands-parents</span>.
                </h1>
                <p className="mb-8 max-w-2xl text-lg leading-8 text-foreground/72 md:text-xl">
                  Partez d’une photo des petits-enfants, d’un portrait ancien ou d’un lieu chargé de souvenirs. Choisissez ensuite un style artistique pour créer un tableau personnel et facile à exposer.
                </p>
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button variant="premium" size="lg" className="h-14 rounded-full px-8 text-base shadow-glow" asChild>
                    <Link to={createPath('hero_primary')} onClick={() => trackGrandparentsCta('hero_primary')}>
                      Créer leur cadeau photo
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" className="h-14 rounded-full border-white/70 bg-white/60 px-8 text-base" asChild>
                    <Link to="/blog/cadeau-grands-parents-photo-famille">Lire le guide famille</Link>
                  </Button>
                </div>
              </div>

              <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-6 shadow-luxury backdrop-blur-xl">
                <img
                  src="/site-assets/hero-family-mobile.jpg"
                  alt="Portrait de famille présenté comme idée de cadeau photo personnalisé pour des grands-parents"
                  width={800}
                  height={640}
                  className="mb-6 aspect-[5/4] w-full rounded-[1.5rem] object-cover"
                />
                <Gift className="mb-5 h-10 w-10 text-primary" />
                <h2 className="font-display text-4xl leading-none text-foreground">Le bon point de départ : une photo qui réveille immédiatement un souvenir.</h2>
                <div className="mt-6 space-y-4">
                  {photoChecklist.map((tip) => (
                    <div key={tip} className="flex gap-3 text-sm leading-7 text-foreground/68">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="mb-10 max-w-3xl">
              <p className="eyebrow mb-3">Choix rapide</p>
              <h2 className="section-title text-foreground">Quelle photo offrir à ses grands-parents ?</h2>
              <p className="mt-4 text-lg leading-8 text-foreground/65">
                Le cadeau devient plus personnel quand la photo correspond à un souvenir précis plutôt qu’à une image seulement décorative.
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {photoIdeas.map((idea) => (
                <article key={idea.title} className="rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
                    <idea.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-3xl leading-none text-foreground">{idea.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Photo conseillée :</span> {idea.photo}</p>
                  <p className="mt-3 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Style :</span> {idea.style}</p>
                  <Button variant="premium" className="mt-6 rounded-full" asChild>
                    <Link to={createPath(idea.source)} onClick={() => trackGrandparentsCta(idea.source)}>
                      Créer à partir de cette idée
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-8 shadow-card backdrop-blur-md">
                <Sparkles className="mb-5 h-8 w-8 text-primary" />
                <h2 className="section-title text-foreground">Un souvenir familial qui ne ressemble pas à un cadeau générique.</h2>
                <p className="mt-5 text-sm leading-7 text-foreground/68">
                  Le sujet principal n’est pas la technologie ni l’effet artistique : c’est la personne, le lieu ou le moment que vos grands-parents auront plaisir à retrouver au quotidien.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {planningSteps.map((step) => (
                  <div key={step.title} className="rounded-[1.5rem] border border-white/65 bg-white/60 p-5 shadow-card">
                    <h3 className="font-display text-2xl leading-none text-foreground">{step.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-foreground/62">{step.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div className="max-w-3xl">
                <p className="eyebrow mb-3">Styles conseillés</p>
                <h2 className="section-title text-foreground">Quel style pour un cadeau photo familial ?</h2>
              </div>
              <Button variant="outline" className="rounded-full border-white/70 bg-white/60" asChild>
                <Link to={createPath('style_section')} onClick={() => trackGrandparentsCta('style_section')}>Tester avec ma photo</Link>
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {styleSuggestions.map((style) => (
                <Link key={style.to} to={style.to} className="group rounded-[1.6rem] border border-white/65 bg-white/60 p-5 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                  <Palette className="mb-4 h-6 w-6 text-primary" />
                  <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{style.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-foreground/62">{style.helper}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary">
                    Voir le style
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="grid gap-5 md:grid-cols-3">
              {relatedPages.map((page) => (
                <Link key={page.to} to={page.to} className="group rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                  <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{page.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-foreground/65">{page.helper}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                    Continuer le guide
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section-shell pb-24 pt-10">
          <div className="container mx-auto px-6 text-center">
            <div className="mx-auto max-w-3xl rounded-[2.2rem] border border-white/65 bg-white/70 p-8 shadow-card backdrop-blur-md md:p-10">
              <Camera className="mx-auto mb-5 h-8 w-8 text-primary" />
              <h2 className="font-display text-4xl leading-none text-foreground md:text-5xl">Prêt à transformer leur souvenir en tableau ?</h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-foreground/65">
                Importez la photo de famille, choisissez un style artistique, puis comparez le rendu avant de sélectionner un support.
              </p>
              <Button variant="premium" size="lg" className="mt-7 h-14 rounded-full px-8" asChild>
                <Link to={createPath('final_cta')} onClick={() => trackGrandparentsCta('final_cta')}>
                  Commencer le cadeau
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  </>
);

export default CadeauGrandsParentsPhotoPersonnalise;
